export const sampleMarkdownLines = [
  "![Convert MD to PDF](/logo.svg){width=50px height=50px}",
  "",
  "# Write in Markdown. Shape the final page.",
  "",
  "Create polished A4 or Letter PDFs with instant preview—**no account, watermark, or custom CSS required**.",
  "",
  "Try **bold**, *italics*, <u>underline</u>, ~~strikethrough~~, ==highlighting==, `inline code`, x^2^, :sparkles:, and [links](https://example.com).",
  "",
  "## Contents",
  "",
  "## Create your PDF",
  "",
  "1. Write or paste Markdown",
  "2. Choose the page size, typeface, orientation, and margins",
  "3. Review and select **Download PDF**",
  "",
  "Supports **A4** or **Letter**, portrait or landscape, adjustable font sizes, margins, and page numbers.",
  "",
  "## Made for",
  "",
  "- Notes, reports, résumés, and handouts",
  "- Technical drafts with code, math, or diagrams",
  "",
  "## Feature sampler",
  "",
  "### Tasks",
  "",
  "- [x] Add content",
  "- [x] Review preview",
  "- [ ] Download PDF",
  "",
  "### Code {#code-example}",
  "",
  "```ts",
  "const prepare = (title: string) => `${title} is ready for PDF.`;",
  "```",
  "",
  "### Table",
  "",
  "| Document | Layout |",
  "| :--- | :--- |",
  "| Notes | A4 portrait |",
  "| Reports | A4 landscape |",
  "",
  "### Diagram",
  "",
  "```mermaid",
  "flowchart LR",
  "  A[Write] --> B[Preview] --> C[Adjust] --> D[Download]",
  "```",
  "",
  "### Math",
  "",
  "Inline math: $a^2+b^2=c^2$",
  "",
  "$$",
  "\\text{Average}=\\frac{\\sum_{i=1}^{n}x_i}{n}",
  "$$",
  "",
  "### Reference",
  "Footnotes keep supporting details separate.[^preview]",
  "",
  "### Alerts and HTML",
  "",
  "> [!NOTE]",
  "> GitHub-style alerts work in the preview and PDF.",
  "",
  "<details>",
  "<summary>More detail</summary>",
  "",
  "Extra detail with H<sub>2</sub>O and E=mc<sup>2</sup>.",
  "",
  "</details>",
  "",
  "Replace this sample, adjust the settings, and download your PDF.",
  "",
  "[^preview]: Layout changes appear in the preview before downloading.",
];

export const sampleMarkdown = sampleMarkdownLines.join("\n");

export const sampleTechResumeLines = [
  "# Alex Rivera",
  "",
  "**Senior Software Engineer** · San Francisco Bay Area · Remote",
  "",
  "[alex.rivera@example.com](mailto:alex.rivera@example.com) · [(415) 555-0142](tel:+14155550142) · [linkedin.com/in/alexrivera](https://linkedin.com/in/alexrivera) · [github.com/alexrivera](https://github.com/alexrivera)",
  "",
  "## Summary",
  "",
  "Full-stack engineer with 8+ years shipping reliable web platforms. Comfortable owning features end-to-end—from API design and data models to polished React UIs—and mentoring teammates through design reviews and incident response.",
  "",
  "## Experience",
  "",
  "### Senior Software Engineer — Northwind Labs",
  "*Jan 2022 – Present · San Francisco, CA*",
  "",
  "- Led rewrite of the billing dashboard in **Next.js** and **TypeScript**, cutting median page load by ~40% and reducing support tickets related to invoices.",
  "- Designed and shipped a multi-tenant **PostgreSQL** schema with row-level security; supported 200+ organizations without per-tenant databases.",
  "- Built CI pipelines with Playwright smoke tests and preview deployments; mean time to production for UI changes dropped from days to hours.",
  "- Mentored three mid-level engineers through quarterly goals, code review standards, and on-call rotations.",
  "",
  "### Software Engineer — Cascade Analytics",
  "*Mar 2019 – Dec 2021 · Remote*",
  "",
  "- Owned the real-time metrics API (**Node.js**, **Redis**, **Kafka**) serving ~15k events/sec with p99 latency under 80ms.",
  "- Introduced typed client SDKs and OpenAPI contracts; reduced integration bugs reported by partner teams by ~60%.",
  "- Partnered with design to launch an interactive charting UI in **React** and **D3**, used daily by 2k+ analysts.",
  "",
  "### Junior Software Engineer — Harbor Systems",
  "*Jun 2017 – Feb 2019 · Oakland, CA*",
  "",
  "- Maintained internal admin tools and REST services in **Python** (**Flask**) and **PostgreSQL**.",
  "- Automated weekly reporting jobs; replaced manual spreadsheet workflows for three operations teams.",
  "",
  "## Projects",
  "",
  "- **markdown2pdf** — Personal toolkit for GitHub-flavored Markdown → paginated PDF with live preview.",
  "- **ops-board** — Open-source status page with incident timelines, Slack alerts, and public RSS feeds.",
  "",
  "## Skills",
  "",
  "| Area | Tools |",
  "| :--- | :--- |",
  "| Languages | TypeScript, JavaScript, Python, SQL |",
  "| Frontend | React, Next.js, Tailwind CSS, Playwright |",
  "| Backend | Node.js, PostgreSQL, Redis, Kafka |",
  "| Cloud & DevOps | AWS, Docker, GitHub Actions, Terraform |",
  "",
  "## Education",
  "",
  "**B.S. Computer Science** — University of California, Santa Cruz · 2017",
];

export const sampleTechResumeMarkdown = sampleTechResumeLines.join("\n");
export const sampleTechResumeFilename = "alex-rivera-resume";

export const sampleReadmeLines = [
  "![Example CLI](/logo.svg){width=48px height=48px}",
  "",
  "# Example CLI",
  "",
  "Ship preview environments from your terminal—**fast, scriptable, and easy to automate**.",
  "",
  "[![CI](https://img.shields.io/badge/CI-passing-brightgreen.svg)](https://example.com/ci) [![License: MIT](https://img.shields.io/badge/License-MIT-blue.svg)](https://opensource.org/licenses/MIT) [![npm](https://img.shields.io/badge/npm-example--cli-orange.svg)](https://example.com/npm)",
  "",
  "## Why Example CLI?",
  "",
  "- Spin up isolated previews for every pull request",
  "- Reuse the same config locally and in CI",
  "- Export status as JSON for dashboards and bots",
  "",
  "## Install",
  "",
  "```bash",
  "npm install -g example-cli",
  "example-cli --version",
  "```",
  "",
  "## Quick start",
  "",
  "1. Authenticate once: `example-cli login`",
  "2. Link a repo: `example-cli init`",
  "3. Launch a preview: `example-cli up`",
  "",
  "## Configuration",
  "",
  "Example CLI reads `.example-cli.toml` from the repo root:",
  "",
  "```toml",
  "[project]",
  'name = "storefront"',
  'runtime = "node22"',
  "",
  "[preview]",
  "port = 3000",
  'build = "npm ci && npm run build"',
  'start = "npm run start"',
  "```",
  "",
  "## Commands",
  "",
  "| Command | Description |",
  "| :--- | :--- |",
  "| `example-cli up` | Create or refresh a preview |",
  "| `example-cli down` | Tear down the active preview |",
  "| `example-cli status` | Show URL, age, and health |",
  "| `example-cli logs` | Stream application logs |",
  "",
  "## Architecture",
  "",
  "```mermaid",
  "flowchart LR",
  "  CLI[Example CLI] --> API[Control plane]",
  "  API --> Runner[Preview runner]",
  "  Runner --> Edge[Edge URL]",
  "```",
  "",
  "## Development",
  "",
  "```bash",
  "git clone https://github.com/example/example-cli.git",
  "cd example-cli",
  "npm ci",
  "npm test",
  "```",
  "",
  "## Contributing",
  "",
  "Issues and pull requests are welcome. Please open an issue before large changes so we can align on scope.",
  "",
  "## License",
  "",
  "MIT © Example CLI contributors",
];

export const sampleReadmeMarkdown = sampleReadmeLines.join("\n");
export const sampleReadmeFilename = "example-cli-readme";

export const sampleChatgptLines = [
  "# ChatGPT export — Weekend meal plan",
  "",
  "*Exported conversation · sample for PDF preview*",
  "",
  "## User",
  "",
  "Plan simple dinners for Friday through Sunday for two people. Prefer 30 minutes or less, and include a grocery list.",
  "",
  "## ChatGPT",
  "",
  "Here is a light weekend plan with one leftover-friendly lunch idea.",
  "",
  "### Dinners",
  "",
  "| Day | Meal | Time |",
  "| --- | --- | --- |",
  "| Friday | Lemon garlic pasta with spinach | 25 min |",
  "| Saturday | Sheet-pan chicken and vegetables | 30 min |",
  "| Sunday | Veggie omelets with toast | 20 min |",
  "",
  "### Grocery list",
  "",
  "1. Pasta, eggs, and a loaf of bread",
  "2. Chicken thighs, spinach, and mixed vegetables",
  "3. Lemon, garlic, olive oil, and salt",
  "",
  "## User",
  "",
  "Any tips so Saturday leftovers work for lunch?",
  "",
  "## ChatGPT",
  "",
  "- Cook extra vegetables on the sheet pan",
  "- Pack leftovers in one container before you sit down to eat",
  "- Add a handful of fresh spinach when reheating",
  "",
  "> Tip: Replace this sample with your own ChatGPT Markdown export, then download the PDF.",
];

export const sampleChatgptMarkdown = sampleChatgptLines.join("\n");
export const sampleChatgptFilename = "sample-chatgpt-export";

export const sampleGeminiLines = [
  "# Gemini export — Weekend trip packing",
  "",
  "*Exported conversation · sample for PDF preview*",
  "",
  "## User",
  "",
  "I'm packing for a two-night weekend trip by train. Give me a practical packing checklist and a quick day-by-day outline.",
  "",
  "## Gemini",
  "",
  "Keep the bag light: one outfit per day, one spare layer, and a small toiletry kit.",
  "",
  "### Packing checklist",
  "",
  "- [ ] Ticket, ID, and phone charger",
  "- [ ] Two day outfits plus one warm layer",
  "- [ ] Comfortable walking shoes",
  "- [ ] Toiletries and any medicines",
  "",
  "### Rough outline",
  "",
  "| Day | Focus |",
  "| :--- | :--- |",
  "| Friday | Arrive, check in, neighborhood walk |",
  "| Saturday | Main outing and a relaxed dinner |",
  "| Sunday | Slow morning, then train home |",
  "",
  "## User",
  "",
  "Anything I should leave at home?",
  "",
  "## Gemini",
  "",
  "- Extra shoes you will not wear",
  "- Full-size toiletries when travel sizes will do",
  "- Work laptop unless you truly need it",
  "",
  "> Tip: Paste your Gemini chat Markdown here and export a clean PDF.",
];

export const sampleGeminiMarkdown = sampleGeminiLines.join("\n");
export const sampleGeminiFilename = "sample-gemini-export";

export const sampleMermaidLines = [
  "# Sprint planning notes",
  "",
  "Compact Mermaid examples for a product team board.",
  "",
  "## Feature pipeline",
  "",
  "```mermaid",
  "flowchart LR",
  "  Idea --> Spec --> Build --> Ship",
  "```",
  "",
  "## Work-item mix",
  "",
  "```mermaid",
  "pie title Current sprint",
  "  \"Done\" : 45",
  "  \"In progress\" : 30",
  "  \"Blocked\" : 10",
  "  \"Backlog\" : 15",
  "```",
  "",
  "## Reminders",
  "",
  "- Prefer left-to-right flows when printing",
  "- Keep node labels short",
  "- Split large diagrams into separate fences",
];

export const sampleMermaidMarkdown = sampleMermaidLines.join("\n");
export const sampleMermaidFilename = "sample-mermaid-diagrams";

export const initialDocumentFilename = "";
export const sampleHomeFilename = "sample-pdf";
export const documentTitle = "Convert MD to PDF";
// Keep the preview responsive after a pause without rebuilding the document
// for every keystroke. Pagination remains isolated in the inactive iframe.
export const previewDebounceMs = 80;
/** Quiet idle before starting the next Paged flight once free. */
export const pagedQuietDebounceMs =
  process.env.NODE_ENV === "test" ? 20 : 400;
/** Max time dirty content can wait (while typing) before a free flight starts. */
export const pagedMaxWaitMs = process.env.NODE_ENV === "test" ? 50 : 1_500;
export const preferencesCommitDelayMs = 300;
export const editorHistoryLimit = 80;
export const editorIndent = "  ";
export const listPrefixPattern = /^(?:- \[[ xX]\]\s+|[-*+]\s+|\d+\.\s+)/;
/** ATX headings (`#`–`######`) with optional CommonMark indent (0–3 spaces). */
export const atxHeadingLinePattern = /^\s{0,3}#{1,6}\s+/;
/** Opening/closing fenced code block markers (` ``` ` or `~~~`). */
export const fenceLinePattern = /^\s{0,3}(`{3,}|~{3,})/;

/**
 * Line indexes that should show the editor-only heading underline.
 * Skips ATX-looking lines inside fenced code so shell comments etc. stay plain.
 */
export function getEditorHeadingLineIndexes(
  lines: readonly string[],
): ReadonlySet<number> {
  const indexes = new Set<number>();
  let inFence = false;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? "";

    if (fenceLinePattern.test(line)) {
      inFence = !inFence;
      continue;
    }

    if (!inFence && atxHeadingLinePattern.test(line)) {
      indexes.add(index);
    }
  }

  return indexes;
}
